import React, { useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import ahoy from 'ahoy.js';
import client from '../lib/apolloClient';
import AppLayout from '../components/Succeedo/Layout/AppLayout';
import ValuationStudioWizard from '../components/Succeedo/ValuationStudio/ValuationStudioWizard';
import ValuationSnapshotResult from '../components/Succeedo/ValuationStudio/ValuationSnapshotResult';
import ShareSnapshotModal from '../components/Succeedo/DealTeam/ShareSnapshotModal';
import { GEORGE_CONVERSATION_FEATURE } from '../constants/succeedoConstants';
import { DEFAULT_COACH_NAME } from '../components/Succeedo/Conversation/copy';

const SET_PRIMARY_SNAPSHOT = gql`
  mutation SetPrimaryValuationSnapshot($id: ID!) {
    set_primary_valuation_snapshot(id: $id) {
      snapshot
      errors
    }
  }
`;

const CREATE_SMB_VALUATION = gql`
  mutation CreateSmbValuation(
    $revenue: Float!
    $operating_expenses: Float!
    $recurring_revenue_pct: Float
    $adjustments: JSON
    $growth_rate: Float
    $historical_revenues: [Float!]
    $tangible_assets: Float
    $liabilities: Float
    $industry_naics_code: String
    $sector_cluster: String
    $province: String
    $country: String
    $state: String
    $entity_type: String
    $is_owner_operated: Boolean
    $owner_total_comp: Float
  ) {
    create_smb_valuation(
      revenue: $revenue
      operating_expenses: $operating_expenses
      recurring_revenue_pct: $recurring_revenue_pct
      adjustments: $adjustments
      growth_rate: $growth_rate
      historical_revenues: $historical_revenues
      tangible_assets: $tangible_assets
      liabilities: $liabilities
      industry_naics_code: $industry_naics_code
      sector_cluster: $sector_cluster
      province: $province
      country: $country
      state: $state
      entity_type: $entity_type
      is_owner_operated: $is_owner_operated
      owner_total_comp: $owner_total_comp
    ) {
      valuation
      errors
    }
  }
`;

const SAVE_SNAPSHOT = gql`
  mutation SaveValuationSnapshot(
    $inputs: JSON!
    $results: JSON!
    $industry_naics_code: String
    $sector_cluster: String
    $benchmark_family: String
  ) {
    save_valuation_snapshot(
      inputs: $inputs
      results: $results
      industry_naics_code: $industry_naics_code
      sector_cluster: $sector_cluster
      benchmark_family: $benchmark_family
    ) {
      snapshot
      errors
    }
  }
`;

const GET_USER_CONTEXT = gql`
  query ValuationUserContext {
    currentUser {
      id
      succeedo_persona
      industry_sector
      deal_teams {
        id
        name
        status
      }
    }
    company {
      id
      features
    }
    coaching_conversation {
      id
      coach_name
    }
    partner_suggestions
    valuation_snapshots(limit: 5)
    introduction_requests
    partners(category: "cbv") {
      id
      name
      org_name
      partner_category
      geography_regions
      bio
      avatar_url
      rating
      verified
    }
    qbo_synced_financials {
      revenue
      expenses_by_category {
        cogs
        operating_expenses
        owner_compensation
        one_time_expenses
      }
      historical_revenues
      synced_at
      qbo_sourced
    }
  }
`;

const CREATE_INTRO_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $coachProfileId: ID) {
    create_introduction_request(partner_category: $partnerCategory, coach_profile_id: $coachProfileId) {
      introduction_request
      errors
    }
  }
`;

function ValuationFlow() {
  const [valuationResult, setValuationResult] = useState(null);
  const [createValuation, { loading }] = useMutation(CREATE_SMB_VALUATION);
  const [saveSnapshot] = useMutation(SAVE_SNAPSHOT);
  const [setPrimarySnapshot] = useMutation(SET_PRIMARY_SNAPSHOT);
  const [currentSnapshotId, setCurrentSnapshotId] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRO_REQUEST);
  // cache-first: the user-context query is read-mostly. The backend caches
  // qbo_synced_financials per-user (server-side cache), and we still expose
  // refetchContext for explicit invalidation after writes (snapshot save,
  // intro request, sync). Also avoids a network round-trip on every wizard
  // remount — see todo 132.
  const { data: contextData, refetch: refetchContext } = useQuery(GET_USER_CONTEXT, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const userContext = contextData?.currentUser || {};
  const partnerSuggestions = contextData?.partner_suggestions || [];
  const savedSnapshots = contextData?.valuation_snapshots || [];
  const introRequests = contextData?.introduction_requests || [];
  const cbvPartners = contextData?.partners || [];
  const qboFinancials = contextData?.qbo_synced_financials || null;

  // Active deal team — server-side filtered to status='active' on PublicUserType.
  // First row (if any) is the user's active team.
  const activeDealTeam = contextData?.currentUser?.deal_teams?.[0] || null;
  // Owners with the coach (flag on, not a buyer) can take a result to the
  // conversation; ?ask= prefills the composer (SucceedoGeorgeContainer).
  const hasCoach = (contextData?.company?.features || []).includes(GEORGE_CONVERSATION_FEATURE)
    && String(userContext.succeedo_persona || '').toLowerCase() !== 'buyer';
  const askCoach = hasCoach
    ? { name: contextData?.coaching_conversation?.coach_name || DEFAULT_COACH_NAME, href: '/succeedo/george?ask=valuation_range' }
    : null;

  // Compute the preselected snapshot from the saved-snapshot list. Only present
  // once the user has saved (i.e., currentSnapshotId resolves to a row in
  // savedSnapshots). Label format mirrors ShareableSnapshotPreview#label for
  // valuation_snapshot so the modal looks consistent with the workspace flow.
  const currentSnapshot = savedSnapshots.find((s) => s.id === currentSnapshotId);
  const preselectedSnapshot = currentSnapshot
    ? {
        id: currentSnapshot.id,
        type: 'valuation_snapshot',
        label: `Valuation — ${new Date(currentSnapshot.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}${currentSnapshot.is_primary ? ' (primary)' : ''}`,
        is_primary: !!currentSnapshot.is_primary,
      }
    : null;

  const handleRequestIntroduction = async (category) => {
    try {
      const { data: result } = await createIntroRequest({
        variables: { partnerCategory: category },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Intro request errors:', errors);
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_introduction_requested', {
          partner_category: category,
          source: 'valuation_results',
        });
      }
      refetchContext();
    } catch (e) {
      console.error('Failed to create intro request', e);
    }
  };

  const handleSubmit = async (inputs) => {
    try {
      const { data } = await createValuation({
        variables: {
          revenue: inputs.revenue,
          operating_expenses: inputs.operatingExpenses,
          recurring_revenue_pct: inputs.recurringRevenuePct,
          adjustments: inputs.adjustments,
          growth_rate: inputs.growthRate,
          historical_revenues: inputs.historicalRevenues,
          tangible_assets: inputs.tangibleAssets,
          liabilities: inputs.liabilities,
          industry_naics_code: inputs.industryNaicsCode,
          sector_cluster: inputs.sectorCluster,
          province: inputs.province,
          is_owner_operated: inputs.isOwnerOperated || false,
          owner_total_comp: inputs.ownerTotalComp || 0,
        },
      });

      if (data?.create_smb_valuation?.errors?.length > 0) {
        console.error('Valuation errors:', data.create_smb_valuation.errors);
        return;
      }

      // Provenance now ships from the server as `valuation.data_sources`
      // (todo 125 — derived inside SmbValuationService and persisted on the
      // snapshot's `results['data_sources']`). We still locally merge `cogs`
      // because the wizard's COGS/OpEx split is a client-only convenience
      // for the cost-structure breakdown — the backend mutation only needs
      // the combined operating_expenses.
      const valuation = data.create_smb_valuation.valuation || {};
      setValuationResult({
        ...valuation,
        inputs: {
          ...(valuation?.inputs || {}),
          cogs: inputs.cogs || 0,
        },
      });
      // Surface the anchor lens (sde / ebitda) and owner-operated status so
      // the FeatureMetricsAggregationWorker can split SDE vs EBITDA usage
      // — the SDE rollout reads adoption off these keys.
      const relevance = valuation.method_relevance || {};
      const headlineMethod = ['sde', 'ebitda', 'revenue', 'dcf', 'asset']
        .find((k) => relevance[k]?.badge === 'most_reliable') || 'ebitda';
      ahoy.track('succeedo_valuation_calculated', {
        sector_cluster: valuation.benchmark_info?.sector_cluster,
        range_low: valuation.overall_range?.low,
        range_high: valuation.overall_range?.high,
        headline_method: headlineMethod,
        is_owner_operated: !!inputs.isOwnerOperated,
        sde_present: !!valuation.sde_method,
      });
    } catch (err) {
      console.error('Valuation error:', err);
    }
  };

  const handleSaveSnapshot = async (result) => {
    try {
      const { data } = await saveSnapshot({
        variables: {
          inputs: result.inputs || {},
          results: {
            sde_method: result.sde_method,
            ebitda_method: result.ebitda_method,
            revenue_method: result.revenue_method,
            dcf_method: result.dcf_method,
            asset_method: result.asset_method,
            overall_range: result.overall_range,
            benchmark_info: result.benchmark_info,
            value_acceleration: result.value_acceleration,
            method_relevance: result.method_relevance,
          },
          industry_naics_code: result.benchmark_info?.naics_code,
          sector_cluster: result.benchmark_info?.sector_cluster,
          benchmark_family: result.benchmark_info?.benchmark_family,
        },
      });

      if (data?.save_valuation_snapshot?.errors?.length > 0) {
        console.error('Save errors:', data.save_valuation_snapshot.errors);
        return null;
      }

      // Refetch BEFORE setting the current id so savedSnapshots already
      // contains the new row when preselectedSnapshot recomputes —
      // otherwise savedSnapshots.find(...) misses, preselectedSnapshot
      // stays null, and the "Share with team" button doesn't appear
      // alongside "Saved ✓".
      await refetchContext();
      const savedSnapshot = data.save_valuation_snapshot.snapshot;
      if (savedSnapshot?.id) setCurrentSnapshotId(savedSnapshot.id);
      ahoy.track('succeedo_valuation_saved', { snapshot_id: savedSnapshot?.id });
      return savedSnapshot;
    } catch (err) {
      console.error('Save snapshot error:', err);
      return null;
    }
  };

  const handleLoadSnapshot = async (snapshot) => {
    setCurrentSnapshotId(snapshot.id);
    // Re-run valuation with saved inputs to get CEPA-adjusted results
    // using the latest assessment score (Rule 2: recalculate on-the-fly)
    const inputs = snapshot.inputs || {};
    try {
      const { data } = await createValuation({
        variables: {
          revenue: inputs.revenue || 0,
          operating_expenses: inputs.operating_expenses || 0,
          recurring_revenue_pct: inputs.recurring_revenue_pct || 0,
          adjustments: inputs.adjustments || {},
          growth_rate: inputs.growth_rate,
          historical_revenues: inputs.historical_revenues || [],
          tangible_assets: inputs.tangible_assets || 0,
          liabilities: inputs.liabilities || 0,
          industry_naics_code: inputs.industry_naics_code,
          sector_cluster: inputs.sector_cluster,
          province: inputs.province,
          // The country, state and entity type it was saved under: a later
          // profile change must not recalculate it as another country's.
          country: inputs.country,
          state: inputs.state,
          entity_type: inputs.entity_type,
        },
      });
      const result = data?.create_smb_valuation?.valuation;
      if (result) {
        // Recalculation re-derives provenance server-side, so `data_sources`
        // is always fresh. Carry `cogs` from the saved snapshot since the
        // backend doesn't recompute the COGS/OpEx split (it only needs the
        // combined operating_expenses input).
        setValuationResult({
          ...result,
          inputs: {
            ...(result.inputs || {}),
            cogs: inputs.cogs || 0,
          },
        });
      } else {
        // Fallback: load saved results directly (no assessment recalculation)
        setValuationResult({ ...snapshot.results, inputs: snapshot.inputs });
      }
    } catch (e) {
      // Fallback on error
      setValuationResult({ ...snapshot.results, inputs: snapshot.inputs });
    }
  };

  if (valuationResult) {
    return (
      <>
      <ValuationSnapshotResult
        result={valuationResult}
        partnerSuggestions={partnerSuggestions}
        savedSnapshots={savedSnapshots}
        onBack={() => { setValuationResult(null); setCurrentSnapshotId(null); }}
        onRecalculate={handleSubmit}
        onSaveSnapshot={() => handleSaveSnapshot(valuationResult)}
        onLoadSnapshot={handleLoadSnapshot}
        isPrimary={currentSnapshotId ? savedSnapshots.some((s) => s.id === currentSnapshotId && s.is_primary) : false}
        onSetPrimary={currentSnapshotId ? async () => {
          try {
            await setPrimarySnapshot({ variables: { id: String(currentSnapshotId) } });
            refetchContext();
          } catch (e) { console.error('Failed to set primary', e); }
        } : null}
        onRequestIntroduction={handleRequestIntroduction}
        introRequests={introRequests}
        featuredPartners={cbvPartners}
        introLoading={introLoading}
        dealTeam={activeDealTeam}
        onShareWithTeam={preselectedSnapshot ? () => setShareModalOpen(true) : undefined}
        askCoach={askCoach}
      />
      {activeDealTeam && preselectedSnapshot && (
        <ShareSnapshotModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSuccess={() => {
            // Refetch user context so any UI showing "shared with team"
            // indicators stays in sync after the share completes.
            if (typeof refetchContext === 'function') refetchContext();
          }}
          dealTeamId={String(activeDealTeam.id)}
          dealTeamName={activeDealTeam.name}
          preselectedSnapshot={preselectedSnapshot}
        />
      )}
      </>
    );
  }

  return (
    <AppLayout breadcrumb="VALUATION STUDIO">
      <ValuationStudioWizard
        onSubmit={handleSubmit}
        loading={loading}
        defaultIndustry={userContext.industry_sector}
        savedSnapshots={savedSnapshots}
        onLoadSnapshot={handleLoadSnapshot}
        qboFinancials={qboFinancials}
      />
    </AppLayout>
  );
}

const SucceedoValuationContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <ValuationFlow />
  </ApolloProvider>
);

export default SucceedoValuationContainer;
